import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, X, ExternalLink } from "lucide-react";

const IMAGES = Array.from({ length: 13 }, (_, i) => ({
  src: `/calendar-${i + 1}.png`,
  label: `Calendar ${i + 1}`,
}));

export default function CalendarGallery() {
  const [fullscreen, setFullscreen] = useState(null);

  useEffect(() => {
    document.title = "Calendar Gallery · Review Images | Temple Franklin";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  return (
    <div className="bg-[#efeae7] min-h-screen pb-16" data-testid="calendar-gallery-page">
      <header className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-xl border-b border-[#CCFBF1]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/social"
            className="text-[#25a1c2] hover:text-[#042F2E] text-sm font-medium inline-flex items-center gap-1.5"
          >
            <ArrowLeft className="w-4 h-4" /> Publish Toolkit
          </Link>
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#25a1c2] bg-[#F0F5F4] px-2.5 py-1 rounded-full">
            Gallery · Private
          </span>
        </div>
      </header>

      <div className="pt-28 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#25a1c2] font-bold mb-3">
              All 13 Calendar Images
            </p>
            <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-[#042F2E] mb-3">
              Your Calendar Art
            </h1>
            <p className="text-[#475569] max-w-2xl mx-auto">
              Tap any image to view it full-size. Tell me which ones you want to use for the 52-Week calendar lead magnet — or say <em>"use them all,"</em> or <em>"I want new ones."</em>
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="calendar-grid">
            {IMAGES.map((img, i) => (
              <button
                key={img.src}
                onClick={() => setFullscreen(img)}
                className="group bg-white rounded-xl border border-[#CCFBF1] overflow-hidden hover:shadow-lg hover:border-[#25a1c2] transition-all"
                data-testid={`calendar-thumb-${i + 1}`}
              >
                <div className="aspect-[3/4] bg-[#F0F5F4] overflow-hidden">
                  <img
                    src={img.src}
                    alt={img.label}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="px-3 py-2 text-center">
                  <p className="font-sans text-xs font-bold text-[#042F2E]">{img.label}</p>
                </div>
              </button>
            ))}
          </div>

          <div className="mt-10 bg-amber-50 border border-amber-200 rounded-2xl p-6 text-sm text-amber-900">
            <p className="font-bold mb-2">📋 How to tell me which ones you want:</p>
            <ul className="space-y-1 list-disc list-inside">
              <li><em>"Use calendars 1, 3, 5, 7 for the lead magnet"</em> — cherry-pick specific ones</li>
              <li><em>"Use them all as a 13-page PDF"</em> — use everything</li>
              <li><em>"Use 1-12 as a 12-month calendar"</em> — month-by-month</li>
              <li><em>"These are old — here are new ones"</em> — send me new files</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Fullscreen lightbox */}
      {fullscreen && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setFullscreen(null)}
          data-testid="lightbox"
        >
          <button
            className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all"
            onClick={(e) => { e.stopPropagation(); setFullscreen(null); }}
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <a
            href={fullscreen.src}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="absolute top-6 left-6 inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-bold rounded-full px-4 py-2 transition-all"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Open raw file
          </a>
          <img
            src={fullscreen.src}
            alt={fullscreen.label}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
          />
        </div>
      )}
    </div>
  );
}
